import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings: siteSettings } = useSiteSettings();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Email-not-confirmed resend state
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [showPassHints, setShowPassHints] = useState(false);

  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/dashboard';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    if (!form.email || !form.password) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);

    const { data: signInData, error: authError } = await supabase.auth.signInWithPassword({
      email: form.email.trim(),
      password: form.password,
    });

    if (authError) {
      setLoading(false);
      const msg = authError.message.toLowerCase();
      const code = (authError as { code?: string }).code ?? '';

      if (
        code === 'invalid_credentials' ||
        code === 'user_not_found' ||
        msg.includes('invalid login') ||
        msg.includes('invalid credentials') ||
        msg.includes('wrong password') ||
        msg.includes('invalid email or password') ||
        msg.includes('email not found')
      ) {
        const next = failedAttempts + 1;
        setFailedAttempts(next);
        setError('Incorrect email or password. Please try again.');
      } else if (
        msg.includes('email not confirmed') ||
        msg.includes('email_not_confirmed') ||
        code === 'email_not_confirmed'
      ) {
        setError('Your email address has not been verified yet.');
        setShowResend(true);
      } else if (msg.includes('too many requests') || code === 'over_request_rate_limit') {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else if (msg.includes('user is banned') || code === 'user_banned') {
        setError('This account has been suspended. Please contact support.');
      } else {
        setError(`Sign in failed: ${authError.message}`);
      }
      return;
    }

    if (signInData?.user) {
      // Check if user has 2FA enabled
      const { data: profile } = await supabase
        .from('profiles')
        .select('totp_enabled')
        .eq('id', signInData.user.id)
        .maybeSingle();

      if (profile?.totp_enabled) {
        // Don't navigate to dashboard yet — require 2FA verification
        const params = redirectTo !== '/dashboard' ? `?redirect=${encodeURIComponent(redirectTo)}` : '';
        navigate(`/2fa/verify${params}`);
        return;
      }
    }
    navigate(redirectTo);
  };

  const handleResendActivation = async () => {
    if (!form.email || resending) return;
    setResending(true);
    setResent(false);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: form.email.trim(),
    });
    setResending(false);
    if (!error) {
      setResent(true);
      setTimeout(() => setResent(false), 8000);
    }
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          {siteSettings.site_logo_url ? (
            <img
              src={siteSettings.site_logo_url}
              alt={siteSettings.site_name || 'Logo'}
              className="h-14 max-w-[200px] mx-auto mb-4 object-contain"
            />
          ) : (
            <img
              src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png"
              alt="API MART"
              className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover"
            />
          )}
          <h1 className="text-2xl font-bold text-white">{siteSettings.site_name || 'API MART'}</h1>
          <p className="text-white/40 text-sm mt-1">ONE API. INFINITE TRANSACTIONS.</p>
        </div>

        <div className="bg-white rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Sign in to your account</h2>

          {/* Error */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
              >
                <i className="ri-error-warning-fill text-red-500 mt-0.5 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Resend activation email banner */}
          <AnimatePresence>
            {showResend && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-amber-600 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-800">Email not verified</p>
                    <p className="text-[11px] text-amber-600 mt-0.5 leading-relaxed">
                      You need to verify your email before signing in. Click below to resend the activation link.
                    </p>
                    {resent ? (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-[11px] font-semibold text-green-700 mt-2 flex items-center gap-1"
                      >
                        <i className="ri-checkbox-circle-fill text-green-500" />
                        Activation email resent! Check your inbox &amp; spam folder.
                      </motion.p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        <button
                          onClick={handleResendActivation}
                          disabled={resending}
                          className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                        >
                          {resending
                            ? <><i className="ri-loader-4-line animate-spin" />Sending…</>
                            : <><i className="ri-refresh-line" />Resend Activation Email</>
                          }
                        </button>
                        <p className="text-[11px] text-amber-600">
                          Wrong email?{' '}
                          <button
                            type="button"
                            onClick={() => { setShowResend(false); setError(''); setForm(f => ({ ...f, email: '' })); }}
                            className="underline cursor-pointer font-semibold"
                          >
                            Clear and re-enter
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Forgot password hint after repeated fails */}
          {failedAttempts >= 2 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3"
            >
              <i className="ri-key-2-line text-amber-500 text-base shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-amber-800">Having trouble signing in?</p>
                <p className="text-[11px] text-amber-600 mt-0.5">Reset your password and get back in seconds.</p>
              </div>
              <Link
                to={`/forgot-password${form.email ? `?email=${encodeURIComponent(form.email)}` : ''}`}
                className="text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors cursor-pointer"
              >
                Reset Password
              </Link>
            </motion.div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Email</label>
              <input
                type="email"
                name="email"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-brand-500 hover:text-brand-600 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => {
                    setForm(f => ({ ...f, password: e.target.value }));
                    setShowPassHints(e.target.value.length > 0 && failedAttempts >= 1);
                  }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>

              {/* Password strength hints — shown after a failed attempt */}
              <AnimatePresence>
                {showPassHints && form.password.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                      <p className="text-[11px] text-gray-500 font-medium mb-1.5">Password must contain:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { label: '6+ characters', ok: form.password.length >= 6 },
                          { label: 'Uppercase letter', ok: /[A-Z]/.test(form.password) },
                          { label: 'Number', ok: /\d/.test(form.password) },
                        ].map(rule => (
                          <span
                            key={rule.label}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors flex items-center gap-0.5 ${rule.ok ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {rule.ok && <i className="ri-check-line" />}{rule.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          {/* T&C notice */}
          <p className="text-[11px] text-gray-400 text-center mt-4 leading-relaxed">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-brand-500 hover:underline font-medium">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="text-brand-500 hover:underline font-medium">Privacy Policy</Link>
          </p>

          <p className="text-center text-xs text-gray-400 mt-3 border-t border-gray-100 pt-3">
            Don&apos;t have an account?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-brand-500 hover:underline cursor-pointer"
            >
              Register
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
